/**
 * @file Bridge functionality for cross-chain operations
 * @module vaults/bridge
 */

import type { Address } from "viem";
import { getVaultByKey } from ".";
import type { BridgeData } from "../api/teller";
import { getPreviewFee } from "../api/teller";
import { type ChainId, toChainId } from "../api/vault-config";
import { NATIVE_TOKEN_FOR_BRIDGE_FEE } from "../constants";
import { TellerAbi } from "../contracts/teller-abi";
import type { VaultKey } from "./config";

/**
 * Parameters for preparing bridge contract arguments
 * @interface PrepareBridgeContractArgParams
 * @property {number} bridgeChainIdentifier - Chain identifier for the bridge protocol
 * @property {Address} userAddress - Ethereum address of the user initiating the bridge
 * @property {Address} [nativeTokenForBridgeFee] - Optional address of the native token used for bridge fees
 */
interface PrepareBridgeContractArgParams {
  bridgeChainIdentifier: number;
  userAddress: Address;
  nativeTokenForBridgeFee?: Address;
}

// Creates a standardized bridge data object for cross-chain transactions
// with default values for gas limits and other bridge protocol parameters
const prepareBridgeContractArg = ({
  bridgeChainIdentifier,
  userAddress,
  nativeTokenForBridgeFee = NATIVE_TOKEN_FOR_BRIDGE_FEE,
}: PrepareBridgeContractArgParams): BridgeData => {
  return {
    chainSelector: bridgeChainIdentifier,
    destinationChainReceiver: userAddress,
    bridgeFeeToken: nativeTokenForBridgeFee,
    messageGas: BigInt(100000),
    data: "0x" as never,
  };
};

/**
 * Parameters for preparing a cross-chain bridge transaction
 * @interface PrepareBridgeTransactionParams
 * @property {VaultKey} vaultKey - Unique identifier for the vault
 * @property {bigint} bridgeAmount - Amount of shares to bridge (in base units)
 * @property {ChainId} sourceChainId - Chain ID where shares currently exist
 * @property {ChainId} destinationChainId - Chain ID where shares will be bridged to
 * @property {Address} userAddress - Ethereum address of the user initiating the bridge
 */
interface PrepareBridgeTransactionParams {
  vaultKey: VaultKey;
  bridgeAmount: bigint;
  sourceChainId: ChainId;
  destinationChainId: ChainId;
  userAddress: Address;
}

/**
 * Transaction data for executing a cross-chain bridge operation
 * @interface BridgeTransactionData
 * @property {typeof TellerAbi} abi - ABI for the CrossChainTeller contract
 * @property {Address} address - Address of the CrossChainTeller contract
 * @property {string} functionName - Name of the function to call on the contract
 * @property {[bigint, BridgeData]} args - Arguments for the bridge function:
 *   [amount, bridgeArgs]
 * @property {number} chainId - ID of the chain where the transaction should be executed
 * @property {bigint} value - Amount of native token to send with the transaction
 */
interface BridgeTransactionData {
  abi: typeof TellerAbi;
  address: Address;
  functionName: "bridge";
  args: [bigint, BridgeData];
  chainId: number;
  value: bigint;
}

// Example usage:
// const bridgeData = await prepareBridgeData({
//   vaultKey: 'bobaeth',
//   bridgeAmount: BigInt('1000000000000000000'), // 1 token
//   sourceChainId: 288, // Boba network
//   userAddress: '0x1234...',
// });
const prepareBridgeTransactionData = async ({
  vaultKey,
  bridgeAmount,
  sourceChainId,
  destinationChainId,
  userAddress,
}: PrepareBridgeTransactionParams): Promise<BridgeTransactionData> => {
  const nativeTokenForBridgeFee = NATIVE_TOKEN_FOR_BRIDGE_FEE;
  const vault = await getVaultByKey(vaultKey);
  if (!vault) {
    throw new Error(`Invalid vault key: ${vaultKey}`);
  }

  if (!vault.contracts) {
    throw new Error(`Contracts not configured for vault ${vaultKey}`);
  }

  if (!vault.contracts.boringVault) {
    throw new Error(
      `BoringVault contract not configured for vault ${vaultKey}`
    );
  }

  if (!vault.contracts.accountant) {
    throw new Error(`Accountant contract not configured for vault ${vaultKey}`);
  }

  if (!vault.contracts.teller) {
    throw new Error(`Teller contract not configured for vault ${vaultKey}`);
  }

  const normalizedSourceChainId = toChainId(sourceChainId);
  const sourceChain = vault.withdraw.sourceChains[normalizedSourceChainId];
  if (!sourceChain) {
    throw new Error(`Source chain not configured for vault ${vaultKey}`);
  }

  const normalizedDestinationChainId = toChainId(destinationChainId);
  const destinationChain =
    sourceChain.destinationChains[normalizedDestinationChainId];
  if (!destinationChain) {
    throw new Error(`Destination chain not configured for vault ${vaultKey}`);
  }

  const bridgeChainIdentifier = destinationChain.bridge.chainIdentifier;

  const bridgeContractArg = prepareBridgeContractArg({
    bridgeChainIdentifier,
    userAddress,
    nativeTokenForBridgeFee,
  });

  const previewFee = await getPreviewFee({
    shareAmount: bridgeAmount,
    bridgeData: bridgeContractArg,
    contractAddress: vault.contracts.teller,
    chainId: normalizedSourceChainId,
  });

  return {
    abi: TellerAbi,
    address: vault.contracts.teller,
    functionName: "bridge",
    args: [bridgeAmount, bridgeContractArg],
    chainId: normalizedSourceChainId,
    value: previewFee,
  };
};

export { prepareBridgeContractArg, prepareBridgeTransactionData };
export type { BridgeTransactionData };
